import { map } from "rxjs/operators";
import { Injectable, inject } from "@angular/core";
import { environment } from "environments/environment";
import { format } from "date-fns";
import { Observable } from "rxjs";

import { LocationMapper } from "../location/location.mapper";
import { Location } from "../location/location.model";
import { FreightRateMapper } from "../quote/quote-freight-rate/models/freight-rate.mapper";
import { FreightRate } from "../quote/quote-freight-rate/models/freight-rate.model";
import { HttpClient, HttpParams } from "@angular/common/http";
import { PaginationList } from "../shared/grid/pagination-list.model";
import { FclRate } from "./rate-fcl.model";
import { LclRate } from "./rate-lcl.model";
import { FclRateLocation } from "./rate-location/rate-location-fcl.model";
import { LclRateLocation } from "./rate-location/rate-location-lcl.model";
import { RateLocationType } from "./rate-location/rate-location-type.model";
import { RateLocationMapper } from "./rate-location/rate-location.mapper";
import { RateLocation } from "./rate-location/rate-location.type";
import { FclRateSurcharge } from "./rate-surcharge/rate-surcharge-fcl.model";
import { LclRateSurcharge } from "./rate-surcharge/rate-surcharge-lcl.model";
import { RateSurchargeMapper } from "./rate-surcharge/rate-surcharge.mapper";
import { RateSurcharge } from "./rate-surcharge/rate-surcharge.type";
import { Rate } from "./rate-type";
import { RateMapper } from "./rate.mapper";
import {
  OnlineCarrierRenewingError,
  onlineRateRenewingCode,
} from "./online-carrier-renewing.error";
import { PaginatedResponse } from "../shared/http/paginated-response.model";
import { lclFormFields } from "../shared/form/fields/lcl-form-fields.const";
import { AirRate } from "./rate-air.model";
import { AirRateLocation } from "./rate-location/rate-location-air.model";
import { AirRateSurcharge } from "./rate-surcharge/rate-surcharge-air.model";
import { airWeightBrackets } from "../shared/form/fields/air-weight-brackets.const";
import { BaseLinkedRate } from "./rate-base-linked.model";
import { FclFlatRate } from "./rate-fcl-flat.model";
import { AirFlatRate } from "./rate-air-flat.model";
import { LclFlatRate } from "./rate-lcl-flat.model";
import { LtlPickupDeliveryRate } from "./rate-ltl-pickup-delivery.model";
import { LtlRate } from "./rate-ltl.model";
import { BaseQuote } from "../quote/models/quote-base.model";
import { BaseStandardQuote } from "../quote/models/quote-base-standard.model";
import { PartnerRateCriteria } from "../quote/quote-freight-rate/models/partner-rate-criteria.model";
import { LtlLinkedRate } from "./rate-ltl-linked.model";
import { FclPickupDeliveryRate } from "./rate-fcl-pickup-delivery.model";
import { NotifyService } from "app/notify/notify.service";
import { TOAST } from "app/notify/toast/toast-type.enum";
import { FtlFlatRate } from "./rate-ftl-flat.model";
import { PtlFlatRate } from "./rate-ptl-flat.model";
import { onlineCarrierType } from "./online-carrier-type.const";
import { OnlineCarrier } from "./online-carrier.interface";

const onlineRateEndpoints: Record<string, string> = {
  [onlineCarrierType.maersk]: "freeOnlineRate",
  [onlineCarrierType.cma]: "cmaOnlineRate",
  [onlineCarrierType.one]: "onlineRate/one",
  [onlineCarrierType.msc]: "onlineRate/msc",
  [onlineCarrierType.hapagLloyd]: "onlineRate/hapag-lloyd",
  [onlineCarrierType.cosco]: "onlineRate/cosco",
  [onlineCarrierType.other]: "onlineRate",
};

// The carrier does not serve the lane: not an error, the caller's "no online rates" message covers it.
const NO_COVERAGE_CODE = "no_coverage";

@Injectable()
export class RateRepository {
  private http = inject(HttpClient);
  private notifyService = inject(NotifyService);

  public getFreightRate(quoteId: number): Observable<FreightRate[]> {
    return this.http
      .get<object[]>(`${environment.apiUrl}/quote/${quoteId}/rate`)
      .pipe(
        map((freightRateList) => freightRateList.map(FreightRateMapper.map)),
      );
  }

  public getOnlineFreightRate(
    quoteId: number,
    originLocation: Location,
    destinationLocation: Location,
    carrierType: string = onlineCarrierType.other,
    originGeoId: string = null,
    destinationGeoId: string = null,
    code: string = null,
  ): Observable<FreightRate[]> {
    let params = new HttpParams();
    if (originGeoId) {
      params = params.append("originGeoId", originGeoId);
    }
    if (destinationGeoId) {
      params = params.append("destinationGeoId", destinationGeoId);
    }

    if (code) {
      params = params.append("criteria[code]", code);
    }

    return this.http
      .get<object[]>(
        `${environment.apiUrl}/quote/${quoteId}/originLocation/${originLocation.getId()}/destinationLocation/${destinationLocation.getId()}/${onlineRateEndpoints[carrierType]}`,
        { params: params },
      )
      .pipe(
        map((freightRateList) => {
          const freightRates: FreightRate[] = [];
          const errors = [];
          const renewing = [];
          for (let i = 0; i < freightRateList.length; i++) {
            // @ts-expect-error - we can expect this type of object here in case ocargo returns errors
            if (freightRateList[i].code === onlineRateRenewingCode) {
              // @ts-expect-error - an error row, as above
              renewing.push(freightRateList[i].reason);
              // @ts-expect-error - we can expect this type of object here in case ocargo returns errors
            } else if (freightRateList[i].reason) {
              // @ts-expect-error - we can expect this type of object here in case ocargo returns errors
              if (freightRateList[i].code !== NO_COVERAGE_CODE) {
                errors.push(
                  // @ts-expect-error - we can expect this type of object here in case ocargo returns errors
                  `${freightRateList[i].service}: ${freightRateList[i].reason}`,
                );
              }
            } else {
              freightRates.push(FreightRateMapper.map(freightRateList[i]));
            }
          }

          if (errors.length) {
            this.notifyService.toast(
              TOAST.ERROR,
              "Error checking online rates",
              errors.join("\n"),
              true,
            );
          }
          if (renewing.length && !freightRates.length) {
            throw new OnlineCarrierRenewingError(renewing.join("\n"));
          }

          return freightRates;
        }),
      );
  }

  public getOnlineCarriers(quoteId: number): Observable<OnlineCarrier[]> {
    return this.http.get<OnlineCarrier[]>(
      `${environment.apiUrl}/quote/${quoteId}/onlineCarrier`,
    );
  }

  // The balance of the quote's organization, readable by anyone who can see the quote.
  public getSearchCredits(quoteId: number): Observable<number> {
    return this.http
      .get<{
        availableCredits: number;
      }>(`${environment.apiUrl}/quote/${quoteId}/searchCredits`)
      .pipe(map((response) => response.availableCredits));
  }

  public getPartnerFreightRate(
    originLocation: Location,
    destinationLocation: Location,
    criteria: PartnerRateCriteria = null,
  ): Observable<FreightRate[]> {
    return this.http
      .get<object[]>(
        `${environment.apiUrl}/quote/remote/1/originLocation/${originLocation.getCode()}/destinationLocation/${destinationLocation.getCode()}/quotissOnlineRate`,
        {
          // A code names a port, an airport and a terminal alike.
          params: (criteria?.toHttpParams() ?? new HttpParams())
            .set("originLocationKind", originLocation.getKind())
            .set("destinationLocationKind", destinationLocation.getKind()),
        },
      )
      .pipe(map((response) => response.map(FreightRateMapper.map)));
  }

  public getPaginatedRateList(
    params: HttpParams = new HttpParams(),
  ): Observable<PaginationList<Rate>> {
    return this.http
      .get<PaginatedResponse>(environment.apiUrl + "/rate", { params: params })
      .pipe(
        map(
          (list) =>
            new PaginationList<Rate>(
              list.items.map(RateMapper.map),
              list.total,
            ),
        ),
      );
  }

  public list(params: HttpParams = new HttpParams()): Observable<Rate[]> {
    return this.http
      .get<object[]>(`${environment.apiUrl}/rate/list`, { params: params })
      .pipe(map((response) => response.map(RateMapper.map)));
  }

  public getRate(id: number): Observable<Rate> {
    return this.http
      .get<object[]>(environment.apiUrl + "/rate/" + id)
      .pipe(map((response) => RateMapper.map(response)));
  }

  public getRateList(
    params: HttpParams = new HttpParams(),
  ): Observable<Rate[]> {
    return this.http
      .get<object[]>(environment.apiUrl + "/rate/list", { params: params })
      .pipe(map((response) => response.map(RateMapper.map)));
  }

  public downloadFile(rate: Rate, type: number): Observable<Blob> {
    const endpoint =
      type === RateLocationType.OriginLocation
        ? "rateOriginLocation"
        : "rateDestinationLocation";

    return this.http
      .get(`${environment.apiUrl}/rate/${rate.getId()}/${endpoint}/download`, {
        responseType: "blob",
      })
      .pipe(
        map((res) => new Blob([res], { type: "application/vnd.ms-excel" })),
      );
  }

  public add(
    rate: Rate,
    quote: BaseQuote = null,
    quoteOriginLocationId: number = null,
    quoteDestinationLocationId: number = null,
  ): Observable<Rate> {
    const type = rate.getType();
    let payload = {
      discriminator: rate.getDiscriminator(),
      name: rate.getName(),
      contractNumber: rate.getContractNumber(),
      carrier: rate.getCarrier() !== null ? rate.getCarrier().getId() : null,
      validFrom: format(rate.getValidity()[0], "yyyy-MM-dd"),
      validTill: format(rate.getValidity()[1], "yyyy-MM-dd"),
      internalNote: rate.getInternalNote(),
      externalNote: rate.getExternalNote(),
      pmNote: rate.getPmNote(),
      deepLink: rate.getDeepLink(),
      status: rate.getStatus(),
      source: rate.getSource(),
    };

    payload = this.extendRatePayloadByContainerLoad(payload, rate);
    payload = this.extendRatePayloadByAgent(payload, rate);

    if (rate instanceof FclFlatRate) {
      return this.http
        .post(`${environment.apiUrl}/rate/fcl-flat`, payload)
        .pipe(map((response) => RateMapper.map(response)));
    }

    if (rate instanceof AirFlatRate) {
      return this.http
        .post(`${environment.apiUrl}/rate/air-flat`, payload)
        .pipe(map((response) => RateMapper.map(response)));
    }

    if (rate instanceof LtlPickupDeliveryRate) {
      return this.http
        .post(
          `${
            environment.apiUrl
          }/rate/ltl-pickup-delivery/${rate.getRangeType()}`,
          payload,
        )
        .pipe(map((response) => RateMapper.map(response)));
    }

    if (rate instanceof FclPickupDeliveryRate) {
      return this.http
        .post(
          `${
            environment.apiUrl
          }/rate/fcl-pickup-delivery/${rate.getRangeType()}`,
          payload,
        )
        .pipe(map((response) => RateMapper.map(response)));
    }

    if (rate instanceof LtlLinkedRate) {
      return this.http
        .post(`${environment.apiUrl}/rate/ltl-linked`, payload)
        .pipe(map((response) => RateMapper.map(response)));
    }

    if (rate instanceof LtlRate) {
      // If quote is provided, adding as a spot rate
      const url =
        quote !== null
          ? `${
              environment.apiUrl
            }/rate/ltl/quote/${quote.getId()}/price/${this.spotPrice(rate.getChargeableWeightPrice())}/${quoteOriginLocationId}/${quoteDestinationLocationId}`
          : `${environment.apiUrl}/rate/ltl`;

      return this.http
        .post(url, payload)
        .pipe(map((response) => RateMapper.map(response)));
    }

    if (rate instanceof LclFlatRate) {
      return this.http
        .post(`${environment.apiUrl}/rate/lcl-flat`, payload)
        .pipe(map((response) => RateMapper.map(response)));
    }

    if (rate instanceof FtlFlatRate) {
      const url =
        quote !== null
          ? `${
              environment.apiUrl
            }/rate/ltl/quote/${quote.getId()}/price/${this.spotPrice(rate.getBlPrice())}/${quoteOriginLocationId}/${quoteDestinationLocationId}`
          : `${environment.apiUrl}/rate/ftl`;

      return this.http
        .post(url, payload)
        .pipe(map((response) => RateMapper.map(response)));
    }

    if (rate instanceof PtlFlatRate) {
      const url =
        quote !== null
          ? `${
              environment.apiUrl
            }/rate/ltl/quote/${quote.getId()}/price/${this.spotPrice(rate.getBlPrice())}/${quoteOriginLocationId}/${quoteDestinationLocationId}`
          : `${environment.apiUrl}/rate/ptl`;
      const quoteSpotPayload =
        quote !== null
          ? {
              name: rate.getName(),
              validFrom: format(rate.getValidity()[0], "yyyy-MM-dd"),
              validTill: format(rate.getValidity()[1], "yyyy-MM-dd"),
              currency: rate.getCurrency(),
            }
          : payload;

      return this.http
        .post(url, quoteSpotPayload)
        .pipe(map((response) => RateMapper.map(response)));
    }

    if (rate instanceof BaseLinkedRate) {
      return this.http
        .post(
          `${environment.apiUrl}/rate/destinationLocation/${rate
            .getDestinationLocation()
            .getLocation()
            .getId()}/originLocation/${rate
            .getOriginLocation()
            .getLocation()
            .getId()}/${type}`,
          payload,
        )
        .pipe(map((response) => RateMapper.map(response)));
    }
  }

  public updateStatus(rate): Observable<Rate> {
    return this.http
      .patch(`${environment.apiUrl}/rate/${rate.getId()}`, {
        status: rate.getStatus(),
      })
      .pipe(map((response) => RateMapper.map(response)));
  }

  public updateLtlSpotRatePrice(
    rate: LtlRate,
    quote: BaseQuote,
  ): Observable<Rate> {
    const url = `${environment.apiUrl}/rate/${rate.getId()}/quote/${quote.getId()}/price/${this.spotPrice(rate.getChargeableWeightPrice())}`;

    return this.http
      .patch(url, {})
      .pipe(map((response) => RateMapper.map(response)));
  }

  public updateFtlSpotRatePrice(
    rate: FtlFlatRate,
    quote: BaseQuote,
  ): Observable<Rate> {
    const url = `${environment.apiUrl}/rate/${rate.getId()}/quote/${quote.getId()}/price/${this.spotPrice(rate.getBlPrice())}`;

    return this.http
      .patch(url, {})
      .pipe(map((response) => RateMapper.map(response)));
  }

  public updatePtlSpotRatePrice(
    rate: PtlFlatRate,
    quote: BaseQuote,
  ): Observable<Rate> {
    const url = `${environment.apiUrl}/rate/${rate.getId()}/quote/${quote.getId()}/price/${this.spotPrice(rate.getBlPrice())}`;
    const payload = {
      name: rate.getName(),
      validFrom: format(rate.getValidity()[0], "yyyy-MM-dd"),
      validTill: format(rate.getValidity()[1], "yyyy-MM-dd"),
      currency: rate.getCurrency(),
    };

    return this.http
      .patch(url, payload)
      .pipe(map((response) => RateMapper.map(response)));
  }

  public simpleUpdateFromQuoteFreightTab(rate: Rate): Observable<Rate> {
    let payload =
      rate instanceof PtlFlatRate
        ? {
            name: rate.getName(),
            validFrom: format(rate.getValidity()[0], "yyyy-MM-dd"),
            validTill: format(rate.getValidity()[1], "yyyy-MM-dd"),
            currency: rate.getCurrency(),
          }
        : {
            name: rate.getName(),
            carrier: rate.getCarrier() ? rate.getCarrier().getId() : null,
            validFrom: format(rate.getValidity()[0], "yyyy-MM-dd"),
            validTill: format(rate.getValidity()[1], "yyyy-MM-dd"),
          };
    payload = this.extendRatePayloadByContainerLoad(payload, rate);
    const notAllowedFields = [
      "pickupRate",
      "deliveryRate",
      "availableWeightBrackets",
      "weightBracketMargins",
    ];

    for (const k in payload) {
      if (notAllowedFields.indexOf(k) >= 0) {
        delete payload[k];
      }
    }

    return this.http
      .patch(`${environment.apiUrl}/rate/${rate.getId()}`, payload)
      .pipe(map((response) => RateMapper.map(response)));
  }

  public update(rate: Rate): Observable<Rate> {
    let payload = {
      name: rate.getName(),
      carrier: rate.getCarrier() ? rate.getCarrier().getId() : null,
      validFrom: format(rate.getValidity()[0], "yyyy-MM-dd"),
      validTill: format(rate.getValidity()[1], "yyyy-MM-dd"),
      internalNote: rate.getInternalNote(),
      externalNote: rate.getExternalNote(),
      externalNoteTranslations: rate.getExternalNoteTranslations(),
      pmNote: rate.getPmNote(),
      deepLink: rate.getDeepLink(),
      status: rate.getStatus(),
      contractNumber: rate.getContractNumber(),
      restrictedUsers: rate.getRestrictedUsers()
        ? rate.getRestrictedUsers().map((user) => user.getId())
        : [],
      restrictedClients: rate.getRestrictedClients()
        ? rate.getRestrictedClients().map((client) => client.getId())
        : [],
    };

    payload = this.extendRatePayloadByContainerLoad(payload, rate);
    payload = this.extendRatePayloadByAgent(payload, rate);

    return this.http
      .patch(`${environment.apiUrl}/rate/${rate.getId()}`, payload)
      .pipe(map((response) => RateMapper.map(response)));
  }

  public delete(rate: Rate): Observable<object> {
    return this.http.delete(`${environment.apiUrl}/rate/${rate.getId()}`);
  }

  public clone(rate: Rate): Observable<Rate> {
    return this.http
      .patch(`${environment.apiUrl}/rate/${rate.getId()}/clone`, {})
      .pipe(map((response) => RateMapper.map(response)));
  }

  public extend(rate: Rate): Observable<Rate> {
    return this.http
      .patch(`${environment.apiUrl}/rate/${rate.getId()}/extend`, {})
      .pipe(map((response) => RateMapper.map(response)));
  }

  public generateBatchExportFile(rates: number[]): Observable<Blob> {
    let params = new HttpParams();
    rates.forEach((id) => {
      params = params.append("rates[]", id + "");
    });

    return this.http
      .get(`${environment.apiUrl}/rate/export/export`, {
        responseType: "blob",
        params: params,
      })
      .pipe(
        map((res) => new Blob([res], { type: "application/vnd.ms-excel" })),
      );
  }

  public generateBatchImportFile(rates: number[]): Observable<Blob> {
    let params = new HttpParams();
    rates.forEach((id) => {
      params = params.append("rates[]", id + "");
    });

    return this.http
      .get(`${environment.apiUrl}/rate/export/import`, {
        responseType: "blob",
        params: params,
      })
      .pipe(
        map((res) => new Blob([res], { type: "application/vnd.ms-excel" })),
      );
  }

  public getDashboardRateLocationList(
    type: number,
    params: HttpParams = new HttpParams(),
  ): Observable<Location[]> {
    let url = "";
    if (type === RateLocationType.OriginLocation) {
      url = environment.apiUrl + "/rateOriginLocation/fcl/list";
    } else if (type === RateLocationType.DestinationLocation) {
      url = environment.apiUrl + "/rateDestinationLocation/fcl/list";
    } else {
      // Exception somehow?
    }

    return this.http
      .get<object[]>(url, { params: params })
      .pipe(map((response) => response.map(LocationMapper.map)));
  }

  public getRateLocationList(
    rateId: number,
    type: number,
    includeBaseLocation = false,
  ): Observable<RateLocation[]> {
    let url = "";
    if (type === RateLocationType.OriginLocation) {
      url = `${environment.apiUrl}/rate/${rateId}/rateOriginLocation`;
    }
    if (type === RateLocationType.DestinationLocation) {
      url = `${environment.apiUrl}/rate/${rateId}/rateDestinationLocation`;
    }

    const params = new HttpParams().set(
      "includeBaseLocation",
      includeBaseLocation + "",
    );

    return this.http
      .get<object[]>(url, { params: params })
      .pipe(map((response) => response.map(RateLocationMapper.map)));
  }

  public addRateLocation(
    rateId: number,
    rateLocation: RateLocation,
    type: number,
  ): Observable<RateLocation> {
    let url = "";
    if (type === RateLocationType.OriginLocation) {
      url = `${environment.apiUrl}/rate/${rateId}/rateOriginLocation`;
    }
    if (type === RateLocationType.DestinationLocation) {
      url = `${environment.apiUrl}/rate/${rateId}/rateDestinationLocation`;
    }

    let payload = {
      active: true,
      location: rateLocation.getLocation().getId(),
      parent: rateLocation.getParentId(),
      comment: rateLocation.getComment(),
    };

    payload = this.extendRateLocationPayloadByContainerLoad(
      payload,
      rateLocation,
    );

    return this.http
      .post(url, payload)
      .pipe(map((response) => RateLocationMapper.map(response)));
  }

  public patchRateLocation(
    rateLocation: RateLocation,
    type: number,
  ): Observable<RateLocation> {
    let url = "";
    if (type === RateLocationType.OriginLocation) {
      url = `${environment.apiUrl}/rateOriginLocation/${rateLocation.getId()}`;
    }
    if (type === RateLocationType.DestinationLocation) {
      url = `${
        environment.apiUrl
      }/rateDestinationLocation/${rateLocation.getId()}`;
    }

    let payload = {
      location: rateLocation.getLocation().getId(),
      parent: rateLocation.getParentId(),
      comment: rateLocation.getComment(),
    };

    payload = this.extendRateLocationPayloadByContainerLoad(
      payload,
      rateLocation,
    );

    return this.http
      .patch(url, payload)
      .pipe(map((response) => RateLocationMapper.map(response)));
  }

  public deleteRateLocation(rateLocationId, type: number): Observable<object> {
    let url = "";
    if (type === RateLocationType.OriginLocation) {
      url = `${environment.apiUrl}/rateOriginLocation/${rateLocationId}`;
    }
    if (type === RateLocationType.DestinationLocation) {
      url = `${environment.apiUrl}/rateDestinationLocation/${rateLocationId}`;
    }

    return this.http.delete(url);
  }

  public getRateSurchargeList(rateId: number): Observable<RateSurcharge[]> {
    return this.http
      .get<object[]>(`${environment.apiUrl}/rate/${rateId}/rateSurcharge`)
      .pipe(map((list) => list.map(RateSurchargeMapper.map)));
  }

  public addRateSurcharge(
    rateId: number,
    surcharge: RateSurcharge,
  ): Observable<RateSurcharge> {
    let payload = {
      surchargeType: surcharge.getSurchargeType().getId(),
      rateOriginLocations: surcharge.getRateOriginLocations()
        ? surcharge.getRateOriginLocations().map((location) => location.getId())
        : [],
      rateDestinationLocations: surcharge.getRateDestinationLocations()
        ? surcharge
            .getRateDestinationLocations()
            .map((location) => location.getId())
        : [],
    };

    payload = this.extendRateSurchargePayloadByContainerLoad(
      payload,
      surcharge,
    );

    return this.http
      .post(
        `${
          environment.apiUrl
        }/rate/${rateId}/rateSurcharge/${surcharge.getDiscriminator()}`,
        payload,
      )
      .pipe(map((response) => RateSurchargeMapper.map(response)));
  }

  public updateRateSurcharge(
    surcharge: RateSurcharge,
  ): Observable<RateSurcharge> {
    let payload = {
      surchargeType: surcharge.getSurchargeType().getId(),
      rateOriginLocations: surcharge.getRateOriginLocations().length
        ? surcharge.getRateOriginLocations().map((location) => location.getId())
        : [],
      rateDestinationLocations: surcharge.getRateDestinationLocations().length
        ? surcharge
            .getRateDestinationLocations()
            .map((location) => location.getId())
        : [],
    };

    payload = this.extendRateSurchargePayloadByContainerLoad(
      payload,
      surcharge,
    );

    return this.http
      .patch(
        `${environment.apiUrl}/rateSurcharge/${surcharge.getId()}`,
        payload,
      )
      .pipe(map((response) => RateSurchargeMapper.map(response)));
  }

  public exportByQuote(quote: BaseStandardQuote): Observable<string> {
    return this.http
      .get<object[]>(environment.apiUrl + "/quote/" + quote.getId() + "/export")
      .pipe(map((response) => response["url"]));
  }

  public deleteRateSurcharge(surchargeId: number): Observable<object> {
    return this.http.delete(
      `${environment.apiUrl}/rateSurcharge/${surchargeId}`,
    );
  }

  public createExclusion(
    rateId: number,
    originLocationId: number,
    destinationLocationId: number,
  ): Observable<RateLocation> {
    return this.http
      .post(
        `${environment.apiUrl}/rate/${rateId}/route/exclude/${originLocationId}/${destinationLocationId}`,
        {},
      )
      .pipe(map((response) => RateLocationMapper.map(response)));
  }

  public excludeAllDestinations(
    rateId: number,
    originLocationId: number,
  ): Observable<RateLocation[]> {
    return this.http
      .post<object[]>(
        `${environment.apiUrl}/rate/${rateId}/route/exclude/rateOriginLocation/${originLocationId}`,
        {},
      )
      .pipe(map((response) => response.map(RateLocationMapper.map)));
  }

  public excludeAllOrigins(
    rateId: number,
    destinationLocationId: number,
  ): Observable<RateLocation[]> {
    return this.http
      .post<object[]>(
        `${environment.apiUrl}/rate/${rateId}/route/exclude/rateDestinationLocation/${destinationLocationId}`,
        {},
      )
      .pipe(map((response) => response.map(RateLocationMapper.map)));
  }

  public deleteExclusion(
    rateId: number,
    originLocationId: number,
    destinationLocationId: number,
  ): Observable<object> {
    return this.http.delete(
      `${environment.apiUrl}/rate/${rateId}/route/exclude/${originLocationId}/${destinationLocationId}`,
      {},
    );
  }

  public includeAllDestinations(
    rateId: number,
    originLocationId: number,
  ): Observable<object> {
    return this.http.delete(
      `${environment.apiUrl}/rate/${rateId}/route/exclude/rateOriginLocation/${originLocationId}`,
      {},
    );
  }

  public includeAllOrigins(
    rateId: number,
    destinationLocationId: number,
  ): Observable<object> {
    return this.http.delete(
      `${environment.apiUrl}/rate/${rateId}/route/exclude/rateDestinationLocation/${destinationLocationId}`,
      {},
    );
  }

  private spotPrice(price: number | string): string {
    return String(price).replace(",", ".");
  }

  private extendRatePayloadByAgent(payload: any, rate: Rate): any {
    if (rate instanceof FclRate || rate instanceof FclFlatRate) {
      payload["agent"] = rate.getAgent() ? rate.getAgent().getId() : null;
    }

    return payload;
  }

  private extendRatePayloadByContainerLoad(payload: any, rate: Rate): any {
    if (rate instanceof BaseLinkedRate) {
      payload["currency"] = rate.getCurrency();
    }

    if (rate instanceof FtlFlatRate) {
      payload["currency"] = rate.getCurrency();
    }

    if (rate instanceof PtlFlatRate) {
      payload["currency"] = rate.getCurrency();
    }

    if (rate instanceof FclFlatRate) {
      payload["incoterms"] = rate.getIncoterms();
      payload["equipment"] = rate.getEquipment();
      payload["pickupDeliveryRate"] = rate.getPickupDeliveryRate()
        ? rate.getPickupDeliveryRate().getId()
        : null;
      payload["availableOnline"] = rate.getAvailableOnline();
      payload["defaultForInternalEmail"] = rate.getDefaultForInternalEmail();
      payload["defaultForExternalEmail"] = rate.getDefaultForExternalEmail();
      payload["availableForPartner"] = rate.getAvailableForPartner();
    }

    if (rate instanceof FclRate) {
      payload["incoterms"] = rate.getIncoterms();
      payload["containers"] = [];
      payload["availableOnline"] = rate.getAvailableOnline();
      payload["defaultForInternalEmail"] = rate.getDefaultForInternalEmail();
      payload["defaultForExternalEmail"] = rate.getDefaultForExternalEmail();
      payload["availableForPartner"] = rate.getAvailableForPartner();
      payload["equipment"] = rate.getEquipment();
      rate.getEquipment().map((containerName) => {
        payload["containers"].push({
          name: containerName,
          value: rate.getContainers().getPrice(containerName),
        });
      });
    }

    if (rate instanceof FclPickupDeliveryRate) {
      payload["availableOnline"] = rate.getAvailableOnline();
      payload["defaultForInternalEmail"] = rate.getDefaultForInternalEmail();
      payload["defaultForExternalEmail"] = rate.getDefaultForExternalEmail();
      payload["availableForPartner"] = rate.getAvailableForPartner();
      payload["equipment"] = rate.getEquipment();
      payload["direction"] = rate.direction;
      payload["currency"] = rate.currency;
      payload["hiddenMarkup"] = rate.hiddenMarkup;
      payload["terminalType"] = rate.terminalType;
    }

    if (rate instanceof LtlPickupDeliveryRate) {
      payload["availableOnline"] = rate.getAvailableOnline();
      payload["cbm"] = rate.cbm;
      payload["ldm"] = rate.ldm;
      payload["ldmThreshold"] = rate.ldmThreshold;
      payload["ldmWidthThreshold"] = rate.ldmWidthThreshold;
      payload["ldmHeightThreshold"] = rate.ldmHeightThreshold;
      payload["ldmLengthThreshold"] = rate.ldmLengthThreshold;
      payload["terminalType"] = rate.terminalType;
      payload["direction"] = rate.direction;
      payload["currency"] = rate.currency;
      payload["availableWeightBrackets"] = rate.availableWeightBrackets;
      payload["hiddenMarkup"] = rate.hiddenMarkup;
      payload["lengthThreshold"] = rate.lengthThreshold;
      payload["lengthExtraCharge"] = rate.lengthExtraCharge;
      payload["lengthPercentageExtraCharge"] = rate.lengthPercentageExtraCharge;
      payload["lengthValueExtraCharge"] = rate.lengthValueExtraCharge;
      payload["minWeight"] = rate.minWeight;
      payload["maxWeight"] = rate.maxWeight;
      payload["maxTaxWeight"] = rate.maxTaxWeight;
      payload["minTaxWeight"] = rate.minTaxWeight;
      payload["maxLength"] = rate.maxLength;
      payload["maxHeight"] = rate.maxHeight;
      payload["maxWidth"] = rate.maxWidth;
      payload["maxLW"] = rate.maxLW;
      payload["maxLWH"] = rate.maxLWH;
      payload["minLength"] = rate.minLength;
      payload["minHeight"] = rate.minHeight;
      payload["minWidth"] = rate.minWidth;
      payload["minLW"] = rate.minLW;
      payload["minLWH"] = rate.minLWH;
      payload["companies"] = rate.companies
        ? rate.companies.map((company) => company["id"])
        : [];
    }

    if (rate instanceof LtlLinkedRate) {
      payload["pickupRate"] = rate.pickupRate ? rate.pickupRate.getId() : null;
      payload["deliveryRate"] = rate.deliveryRate
        ? rate.deliveryRate.getId()
        : null;
      payload["weightBracketMargins"] = rate.weightBracketMargins
        ? rate.weightBracketMargins.getRequestParams()
        : null;
      payload["currency"] = rate.currency;
      payload["availableWeightBrackets"] = rate.availableWeightBrackets;
      payload["hiddenMarkup"] = rate.hiddenMarkup;

      const rates = rate.terminalOrder.filter((x) => x !== null) as number[];
      payload["terminalOrder"] = rates;
      payload["rates"] = rates;
      payload["weightBracketMargins"] = rate.weightBracketMargins
        ? rate.weightBracketMargins.getRequestParams()
        : null;
      payload["companies"] = rate.companies
        ? rate.companies.map((company) => company["id"])
        : [];
    }

    if (rate instanceof LtlRate) {
      payload["cbm"] = rate.cbm;
      payload["ldm"] = rate.ldm;
      payload["ldmThreshold"] = rate.ldmThreshold;
      payload["ldmWidthThreshold"] = rate.ldmWidthThreshold;
      payload["ldmHeightThreshold"] = rate.ldmHeightThreshold;
      payload["ldmLengthThreshold"] = rate.ldmLengthThreshold;
      payload["currency"] = rate.currency;
      payload["pickupRate"] = rate.pickupRate ? rate.pickupRate.getId() : null;
      payload["deliveryRate"] = rate.deliveryRate
        ? rate.deliveryRate.getId()
        : null;
      payload["weightBracketMargins"] = rate.weightBracketMargins
        ? rate.weightBracketMargins.getRequestParams()
        : null;
      payload["availableWeightBrackets"] = rate.availableWeightBrackets;
      payload["hiddenMarkup"] = rate.hiddenMarkup;
      payload["lengthThreshold"] = rate.lengthThreshold;
      payload["lengthExtraCharge"] = rate.lengthExtraCharge;
      payload["lengthPercentageExtraCharge"] = rate.lengthPercentageExtraCharge;
      payload["lengthValueExtraCharge"] = rate.lengthValueExtraCharge;
      payload["minWeight"] = rate.minWeight;
      payload["maxWeight"] = rate.maxWeight;
      payload["maxTaxWeight"] = rate.maxTaxWeight;
      payload["minTaxWeight"] = rate.minTaxWeight;
      payload["maxLength"] = rate.maxLength;
      payload["maxHeight"] = rate.maxHeight;
      payload["maxWidth"] = rate.maxWidth;
      payload["maxLW"] = rate.maxLW;
      payload["maxLWH"] = rate.maxLWH;
      payload["minLength"] = rate.minLength;
      payload["minHeight"] = rate.minHeight;
      payload["minWidth"] = rate.minWidth;
      payload["minLW"] = rate.minLW;
      payload["minLWH"] = rate.minLWH;
      payload["minMargin"] = rate.minMargin;
      payload["maxMargin"] = rate.maxMargin;
      payload["availableOnline"] = rate.availableOnline;
      payload["domestic"] = rate.domestic;
      payload["companies"] = rate.companies
        ? rate.companies.map((company) => company["id"])
        : [];
    }

    if (rate instanceof LclRate) {
      payload[lclFormFields.WEIGHT_PRICE] = rate.getWeightPrice();
      payload[lclFormFields.MEASUREMENT_PRICE] = rate.getMeasurementPrice();
      payload["weightUnit"] = rate.getWeightUnit();
      payload[lclFormFields.MIN_PRICE] = rate.getMinPrice();
      payload[lclFormFields.BL_PRICE] = rate.getBlPrice();
      payload[lclFormFields.CHARGEABLE_WEIGHT_PRICE] =
        rate.getChargeableWeightPrice();
    }

    if (rate instanceof LclFlatRate || rate instanceof LclRate) {
      payload["weightBracketMargins"] = rate.weightBracketMargins
        ? rate.weightBracketMargins.getRequestParams()
        : null;
      payload["availableOnline"] = rate.getAvailableOnline();
      payload["defaultForInternalEmail"] = rate.getDefaultForInternalEmail();
      payload["defaultForExternalEmail"] = rate.getDefaultForExternalEmail();
      payload["availableForPartner"] = rate.getAvailableForPartner();
      payload["minDensity"] = rate.minDensity;
      payload["maxDensity"] = rate.maxDensity;
    }

    if (
      rate instanceof LclFlatRate ||
      rate instanceof LclRate ||
      rate instanceof AirRate ||
      rate instanceof AirFlatRate
    ) {
      payload["incoterms"] = rate.getIncoterms();
      payload["maxWeightPerShipment"] = rate.maxWeightPerShipment;
      payload["maxWeightPerPiece"] = rate.maxWeightPerPiece;
      payload["maxMeasurementPerShipment"] = rate.maxMeasurementPerShipment;
      payload["maxMeasurementPerPiece"] = rate.maxMeasurementPerPiece;
      payload["maxLength"] = rate.maxLength;
      payload["maxHeight"] = rate.maxHeight;
      payload["maxWidth"] = rate.maxWidth;
      payload["maxLWH"] = rate.maxLWH;
      payload["marginType"] = rate.marginType;
    }

    if (rate instanceof AirRate) {
      payload[airWeightBrackets.lt45] = rate.getWeightPriceLt45();
      payload[airWeightBrackets.gt45] = rate.getWeightPriceGt45();
      payload[airWeightBrackets.gt100] = rate.getWeightPriceGt100();
      payload[airWeightBrackets.gt300] = rate.getWeightPriceGt300();
      payload[airWeightBrackets.gt500] = rate.getWeightPriceGt500();
      payload[airWeightBrackets.gt1000] = rate.getWeightPriceGt1000();
      payload[lclFormFields.MIN_PRICE] = rate.getMinPrice();
      payload[lclFormFields.BL_PRICE] = rate.getBlPrice();
      payload[lclFormFields.CHARGEABLE_WEIGHT_PRICE] =
        rate.getChargeableWeightPrice();
    }

    if (rate instanceof AirRate || rate instanceof AirFlatRate) {
      payload["weightBracketMargins"] = rate.weightBracketMargins
        ? rate.weightBracketMargins.getRequestParams()
        : null;
      payload["availableOnline"] = rate.getAvailableOnline();
      payload["defaultForInternalEmail"] = rate.getDefaultForInternalEmail();
      payload["defaultForExternalEmail"] = rate.getDefaultForExternalEmail();
      payload["availableForPartner"] = rate.getAvailableForPartner();
    }

    return payload;
  }

  private extendRateLocationPayloadByContainerLoad(
    payload: any,
    rateLocation: RateLocation,
  ) {
    if (rateLocation instanceof FclRateLocation) {
      payload["containers"] = rateLocation.getContainers().getRequestParams();
    }

    if (rateLocation instanceof LclRateLocation) {
      payload[lclFormFields.WEIGHT_PRICE] = rateLocation.getWeightPrice();
      payload[lclFormFields.MEASUREMENT_PRICE] =
        rateLocation.getMeasurementPrice();
      payload[lclFormFields.MIN_PRICE] = rateLocation.getMinPrice();
      payload[lclFormFields.BL_PRICE] = rateLocation.getBlPrice();
    }

    if (rateLocation instanceof AirRateLocation) {
      payload[airWeightBrackets.lt45] = rateLocation.getWeightPriceLt45();
      payload[airWeightBrackets.gt45] = rateLocation.getWeightPriceGt45();
      payload[airWeightBrackets.gt100] = rateLocation.getWeightPriceGt100();
      payload[airWeightBrackets.gt300] = rateLocation.getWeightPriceGt300();
      payload[airWeightBrackets.gt500] = rateLocation.getWeightPriceGt500();
      payload[airWeightBrackets.gt1000] = rateLocation.getWeightPriceGt1000();
      payload[lclFormFields.MIN_PRICE] = rateLocation.getMinPrice();
    }

    return payload;
  }

  private extendRateSurchargePayloadByContainerLoad(
    payload: any,
    rateSurcharge: RateSurcharge,
  ) {
    if (rateSurcharge instanceof FclRateSurcharge) {
      payload["containers"] = rateSurcharge.getContainers().getRequestParams();
      payload["marginType"] = rateSurcharge.getMarginType();
    }

    if (rateSurcharge instanceof LclRateSurcharge) {
      payload["wmPrice"] = rateSurcharge.getWmPrice();
      payload["blPrice"] = rateSurcharge.getBlPrice();
      payload["minPrice"] = rateSurcharge.getMinPrice();
    }

    if (rateSurcharge instanceof AirRateSurcharge) {
      payload["weightPrice"] = rateSurcharge.getWeightPrice();
    }

    return payload;
  }
}
